import React from "react";
import { useTranslation } from "react-i18next";

import { ButtonIconTrash, InputSelect } from "../../../common/components/form";
import BackNext from "../Wizard/BackNext.jsx";
import LayoutMobileMenu from "./LayoutMobileMenu.jsx";

import { useGetLocales } from "../../hooks";
import { ProductTypeByValue } from "../../lib/product-types";
import RecalculateBlocksId from "../../lib/builder/recalculate-block-ids.js";
import RecalculateBlocksIds from "../../lib/builder/recalculate-block-ids.js";

const LayoutBlocks = {
    BlockMenu: { items: [{ image: "layoutMenu" }], movable: false, deletable: false },
    BlockHeader: {
        items: [
            { image: "layoutHeaderCrossPlatform", value: "cross-platform" },
            { image: "layoutHeaderDesktop", value: "web-product" },
            { image: "layoutHeaderMobileApp", value: "mobile-app" },
            { image: "layoutHeaderServices", value: "services" },
        ],
        movable: true,
        deletable: true,
        editable: true,
    },
    BlockFeatures: { items: [{ image: "layoutFeatures" }], movable: true, deletable: true },
    BlockSlider: { items: [{ image: "layoutSlider" }], movable: true, deletable: true },
    BlockEmbed: { items: [{ image: "layoutSlider" }], movable: true, deletable: true },
    BlockVideo: { items: [{ image: "layoutSlider" }], movable: true, deletable: true },
    BlockSteps: { items: [{ image: "layoutSteps" }], movable: true, deletable: true },
    BlockNumbers: { items: [{ image: "layoutNumbers" }], movable: true, deletable: true },
    BlockOffers: {
        items: [
            { image: "layoutOffersSellingPlans", value: "selling-plans" },
            { image: "layoutOffersTrafficAcquisition", value: "traffic-acquisition" },
        ],
        movable: true,
        deletable: true,
        editable: true,
    },
    BlockFeedbacks: { items: [{ image: "layoutFeedbacks" }], movable: true, deletable: true },
    BlockGetInTouch: { items: [{ image: "layoutFAQ" }], movable: true, deletable: true },
    BlockFooter: { items: [{ image: "layoutFooter" }], movable: false, deletable: false },
    CookiesPopup: { hide: true },
};

export default function LayoutSettings(props) {
    const { t, i18n } = useTranslation();
    const { set, variables, structure } = props;
    const BlockProject = getBlockProject();

    // Init menu items
    const [LayoutMenuItems, setLayoutMenuItems] = React.useState([]);

    // Landing lang selector
    const locales = useGetLocales();

    const handleLanguageChange = lang => {
        set({ Localisation: lang });
    };

    React.useEffect(() => {
        !variables?.Localisation && set({ ...variables, Localisation: variables.Localisation || i18n.language });
    }, [variables]);

    React.useEffect(() => {
        const initialLayoutMenuItems = BlockProject.children.filter((item, index, self) => {
            const name = getBlockName(item);
            const isHide = LayoutBlocks[name]?.movable || false;
            return isHide && self.findIndex(otherItem => getBlockName(otherItem) === name) === index;
        });

        setLayoutMenuItems(initialLayoutMenuItems);

        const counters = {};
        BlockProject.children.forEach(child => {
            if (child.id) {
                const id = child.id.replace(/-\d+$/, "");
                counters[id] = (counters[id] ?? 0) + 1;
                child.id = `${child.id}-${counters[id]}`;
            }
        });

        // Default set
        set(null, createFromStructure(structure));
    }, []);

    function getBlockProject() {
        if (Array.isArray(structure)) {
            if (structure[0].name === "BlockProject") {
                return structure[0];
            }
        } else if (structure?.name === "BlockProject") {
            return structure;
        }
    }

    function createFromStructure(structure) {
        return Array.isArray(structure) ? [...structure] : { ...structure };
    }

    function onElementDelete(index) {
        BlockProject.children = BlockProject.children.filter((_, _index) => _index !== index);

        RecalculateBlocksIds(BlockProject.children);

        set(null, createFromStructure(structure));
    }

    function swapElements(arr, index1, index2) {
        const newArr = [...arr];
        [newArr[index1], newArr[index2]] = [newArr[index2], newArr[index1]];
        return newArr;
    }

    function onElementMoveUp(index) {
        if (BlockProject && index > 0) {
            const newChildren = swapElements(BlockProject.children, index, index - 1);

            // Check posibility of move
            const nextBlockName = getBlockName(BlockProject.children[index - 1]);

            if (!LayoutBlocks[nextBlockName]?.movable) {
                return;
            }

            BlockProject.children = newChildren;
            set(null, createFromStructure(structure));
        }
    }

    function onElementMoveDown(index) {
        if (BlockProject && index < BlockProject.children.length - 1) {
            const newChildren = swapElements(BlockProject.children, index, index + 1);

            // Check posibility of move
            const previousBlockName = getBlockName(BlockProject.children[index + 1]);

            if (!LayoutBlocks[previousBlockName]?.movable) {
                return;
            }

            BlockProject.children = newChildren;
            set(null, createFromStructure(structure));
        }
    }

    function changeProductType(direction, index) {
        const currentElement = BlockProject.children[index];
        const currentImageIndex = currentElement.currentImageIndex;

        const blockInfo = LayoutBlocks[currentElement.name];

        if (blockInfo && blockInfo.items) {
            const blockItems = blockInfo.items;
            let currentIndex = currentImageIndex;

            if (direction === "left") {
                currentIndex = (currentIndex - 1 + blockItems.length) % blockItems.length;
            } else if (direction === "right") {
                currentIndex = (currentIndex + 1) % blockItems.length;
            }

            const childProps =
                getBlockName(currentElement) === "BlockHeader"
                    ? {
                          ...currentElement.props,
                          ...ProductTypeByValue[blockItems[currentIndex].value].variables,
                          ProductType: blockItems[currentIndex].value,
                      }
                    : {
                          ...currentElement.props,
                          // ...ProductTypeByValue[blockItems[currentIndex].value].variables,
                          BusinessGoal: blockItems[currentIndex].value,
                      };

            const updatedChildren = [...BlockProject.children];

            updatedChildren[index] = {
                ...currentElement,
                currentImageIndex: currentIndex,
                // image: blockItems[currentIndex].image,
                props: { ...childProps },
            };

            BlockProject.children = updatedChildren;

            set(null, createFromStructure(structure));
        }
    }

    function onAddItem(item) {
        const newItem = { ...item };

        BlockProject.children.splice(-2, 0, newItem);
        RecalculateBlocksIds(BlockProject.children);

        set(null, createFromStructure(structure));
    }

    function getBlockName(block) {
        return block?.name || block?.children[0]?.name || block?.children?.[0]?.[0]?.name;
    }

    return (
        <div className="layout-wrapper">
            <div className="layout-top-menu">
                <div className="preview-menu-logo">pgbldr.com</div>
                <BackNext {...props} next={null} />
                <LayoutMobileMenu items={LayoutMenuItems} onAddItem={onAddItem} getBlockName={getBlockName} />
            </div>

            <div className="layout-side-menu">
                {LayoutMenuItems.map((child, index) => {
                    const name = getBlockName(child);

                    return (
                        <div className="layout-side-menu-item" key={index} onClick={() => onAddItem(child)}>
                            {t(name)} +
                        </div>
                    );
                })}
            </div>

            <div className="layout-content">
                <div className="layout-title">
                    <h1>{t("WebsiteLayoutHeader")}</h1>

                    <div className="margin-top-40">
                        <p>{t("WebsiteLayoutText")}</p>
                    </div>

                    <div className="layout-lang-select margin-top-40">
                        <p>{t("WebsiteLayoutLanguagePlaceholder")}</p>

                        <div className=" margin-top-20 margin-bottom-20">
                            <InputSelect
                                fullWidth
                                onSelect={handleLanguageChange}
                                options={Object.entries(locales).map(([value, text]) => ({ value, text }))}
                                selected={variables.Localisation || i18n.language}
                            />
                        </div>
                    </div>
                </div>

                {BlockProject?.children?.map((child, index) => {
                    const name = getBlockName(child);
                    const blockInfo = LayoutBlocks[name] || {};
                    const { items, movable, editable = false, deletable, hide = false } = blockInfo;

                    if (hide) {
                        return null;
                    }

                    if (!child.currentImageIndex) {
                        child.currentImageIndex = 0;
                    }

                    const currentImageIndex = child.currentImageIndex || 0;
                    const image = items?.[currentImageIndex]?.image;

                    // Disable arrows up && down
                    const nextElementName = getBlockName(BlockProject.children[index - 1]);
                    const prevElementName = getBlockName(BlockProject.children[index + 1]);
                    const allowUp = LayoutBlocks[nextElementName]?.movable;
                    const allowDown = LayoutBlocks[prevElementName]?.movable;

                    const CNLayoutItem = ["layout-item"];
                    movable && CNLayoutItem.push("movable");

                    return (
                        <div key={index} className={`${CNLayoutItem.join(" ")}`}>
                            <div className="layout-item-move-containter">
                                {movable && (
                                    <>
                                        <div>
                                            {allowUp && (
                                                <div
                                                    className="layout-item-arrow-up"
                                                    onClick={() => onElementMoveUp(index)}
                                                />
                                            )}
                                        </div>

                                        <div>
                                            {allowDown && (
                                                <div
                                                    className="layout-item-arrow-down"
                                                    onClick={() => onElementMoveDown(index)}
                                                />
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="layout-item-image-containter">
                                <div>
                                    {editable && (
                                        <div
                                            className="layout-item-arrow-left"
                                            onClick={() => changeProductType("left", index, items)}
                                        />
                                    )}
                                </div>

                                <img src={`/img/layout-images/${image}.svg`} alt="Header" />

                                <div>
                                    {editable && (
                                        <div
                                            className="layout-item-arrow-right"
                                            onClick={() => changeProductType("right", index, items)}
                                        />
                                    )}
                                </div>
                            </div>

                            <div className="layout-item-text">{t(name)}</div>

                            <div className="layout-item-remove">
                                {deletable && <ButtonIconTrash onClick={() => onElementDelete(index)} />}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
